import {
  Box,
  IconButton,
  CardActions,
  Card as MuiCard,
  CardActionsProps,
  Modal,
} from "@mui/material";
import { Icon } from "../Icon";
import { uniqueId } from "lodash-es";
import Draggable from "react-draggable";
import { forwardRef, useRef, useState, useCallback, memo } from "react";
import { COLOR_CONSTANTS } from "../../constants/styles";
import { BIAS_LAT_LON_RATIO } from "../../constants/constants";
import { CardHeader, CardHeaderProps } from "../CardHeader";
import { MapImageViewer } from "../MapImageViewer";

import { ProjectDetailId } from "../../../../../src/domains/timeseries/TimeSeriesPage.model";
import { useProjectMeta } from "../../../../../src/domains/projects-list/project-query/projects.queries";
import { BaseTileLayer } from "../../../../../src/domains/timeseries/components/Inspection2d/Layers/BaseTileLayer";
import {
  CustomCircle,
  Location,
  Map,
} from "../../../../../src/domains/shared/react-openlayers";

/**
 * 드래그 가능한 부동 이미지 뷰어 — 전체화면 시 react-openlayers 미니맵으로 촬영 위치 표시.
 *
 * `imagePosition` 좌표가 있어야 전체화면 미니맵에 `BoundaryCircle` 마커가 표시된다.
 */
export interface FloatImageViewerProps extends ProjectDetailId {
  fileName: string;
  bookmarked?: boolean;
  onClose?: () => void;
  onFullScreen?: () => void;
  onToggleBookmark?: () => void;
  image?: string;
  /** 본 사진이 도착하기 전 대신 보여줄 저해상 사진(목록에서 이미 받아 둔 썸네일) */
  previewImage?: string;
  alt?: string;
  disabled?: boolean;
  cardActionsProps?: Omit<CardActionsProps, "children">;
  headerProps?: Omit<CardHeaderProps, "fileName" | "size">;
  draggable?: boolean;
  /** 전체화면 미니맵에 표시할 이미지 촬영 위치 (lng/lat) */
  imagePosition?: Location;
}

interface PointedMapProps extends ProjectDetailId {
  center: Location;
}

const PointedMap = memo(
  ({ projectId, timeSeriesId, center }: PointedMapProps) => {
    const { data: projectMeta } = useProjectMeta({ projectId, timeSeriesId });
    const cardRef = useRef<HTMLDivElement>(null);

    return (
      <Draggable
        nodeRef={cardRef}
        handle={`.test`}
        defaultPosition={{ x: 0, y: 0 }}
        bounds="parent"
      >
        <MuiCard
          ref={cardRef}
          sx={{
            ...card,
            maxHeight: "308px",
            width: "272px",
            position: "absolute",
            right: "20px",
            bottom: "20px",
            pointerEvents: "auto",
            zIndex: 1000,
          }}
        >
          <Box className={"test"}>
            <IconButton size="small">
              <Icon name="20/etc/handle" size={20} />
            </IconButton>
          </Box>

          {projectMeta && (
            <Map
              width="272px"
              height="288px"
              isShownOsm={true}
              zoomLevel={20}
              center={center}
            >
              <BaseTileLayer
                orthoMode="mosaic"
                timeSeriesId={timeSeriesId}
                tileMatrix={projectMeta.matrix}
                minZoom={projectMeta.tms_zoom_boundary[0]}
                maxZoom={projectMeta.tms_zoom_boundary[1]}
              />
              <Map.CustomCircle
                zIndex={1000}
                center={center}
                radius={1}
                color="RED"
                colorFill={true}
                hasStroke={false}
              />
            </Map>
          )}
        </MuiCard>
      </Draggable>
    );
  }
);

PointedMap.displayName = "PointedMap";

export const FloatImageViewer = forwardRef<
  HTMLDivElement,
  FloatImageViewerProps
>(
  (
    {
      projectId,
      timeSeriesId,
      fileName,
      bookmarked = false,
      onClose,
      onFullScreen,
      onToggleBookmark,
      image,
      previewImage,
      alt,
      disabled = false,
      headerProps,
      cardActionsProps,
      draggable = true,
      imagePosition,
    },
    ref
  ) => {
    const cardIdRef = useRef<string>(uniqueId("float-viewer-"));
    const headerId = `${cardIdRef.current}-header`;
    const cancelId = `${cardIdRef.current}-cancel`;
    const [isFullScreen, setIsFullScreen] = useState(false);

    const handleFullScreen = useCallback(() => {
      setIsFullScreen(true);
      onFullScreen?.();
    }, [onFullScreen]);

    const handleCloseFullScreen = useCallback(() => {
      setIsFullScreen(false);
    }, []);

    const cardRef = useRef<HTMLDivElement>(null);

    // ref와 cardRef를 병합
    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        cardRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const cardContent = (
      <MuiCard
        ref={mergedRef}
        sx={{
          ...card,
          maxHeight: "308px",
          width: "272px",
          position: "absolute",
          right: "20px",
          bottom: "20px",
          pointerEvents: "auto",
        }}
      >
        <CardHeader
          onClose={onClose}
          size="medium"
          fileName={fileName}
          className={headerId}
          dragCancelId={cancelId}
          {...headerProps}
        />
        <Box sx={imageContainer}>
          {/* 서명 URL 이 아직 없어도 뷰어를 띄운다 — 저해상 사진·로딩 표시를 그 안에서 맡는다 */}
          <MapImageViewer
            imageUrl={image ?? ""}
            previewImageUrl={previewImage}
            height="100%"
          />
        </Box>
        <CardActions sx={cardActions} {...cardActionsProps}>
          <IconButton size="small" onClick={handleFullScreen}>
            <Icon
              size={24}
              clickable
              sx={exitScreenIcon}
              name="etc/full screen"
            />
          </IconButton>
          <IconButton size="small" onClick={onToggleBookmark}>
            {bookmarked ? (
              <Icon name="etc/BookmarkActive" size={24} clickable />
            ) : (
              <Icon name="etc/Bookmark" size={24} sx={bookmarkIcon} clickable />
            )}
          </IconButton>
        </CardActions>
      </MuiCard>
    );

    const cardWithDraggable = draggable ? (
      <Draggable
        nodeRef={cardRef}
        handle={`.${headerId}`}
        cancel={`.${cancelId}`}
        defaultPosition={{ x: 0, y: -250 }}
        bounds="parent"
      >
        {cardContent}
      </Draggable>
    ) : (
      cardContent
    );

    return (
      <>
        {cardWithDraggable}

        <Modal
          open={isFullScreen}
          onClose={handleCloseFullScreen}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "90vw",
              height: "90vh",
              bgcolor: "background.paper",
              boxShadow: 24,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <PointedMap
              projectId={projectId}
              timeSeriesId={timeSeriesId}
              center={imagePosition ?? [0, 0]}
            />

            <MapImageViewer
              imageUrl={image ?? ""}
              previewImageUrl={previewImage}
              height="100%"
              fillContainer
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "absolute",
                bottom: "32px",
                zIndex: 1000,
                width: "100%",
              }}
            >
              <CardHeader
                size="large"
                fileName={fileName}
                bookmarked={bookmarked}
                onPrevious={headerProps?.onPrevious}
                onNext={headerProps?.onNext}
                onBookmark={onToggleBookmark}
                onExpand={handleCloseFullScreen}
                hasNext={headerProps?.hasNext}
                hasPrevious={headerProps?.hasPrevious}
              />
            </Box>
          </Box>
        </Modal>
      </>
    );
  }
);

FloatImageViewer.displayName = "FloatImageViewer";

const imageContainer = {
  width: "100%",
  aspectRatio: BIAS_LAT_LON_RATIO,
  flexShrink: 0,
} as const;

const exitScreenIcon = {
  "& path": {
    strokeWidth: 1,
    strokeOpacity: 1,
    fill: COLOR_CONSTANTS.common.white_states.main,
  },
} as const;

const bookmarkIcon = {
  "& path": {
    fill: "black",
    stroke: "white",
    strokeWidth: 0.8,
    strokeOpacity: 1,
  },
} as const;

const card = {
  position: "relative",
  overflow: "hidden",
} as const;

const cardActions = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  position: "absolute",
  bottom: 0,
  left: 0,
  padding: "2px",
} as const;
